import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery,
  Container
} from "@mui/material";
import {
  Create,
  Person,
  History,
  Logout,
  School,
  MenuBook
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#013a64",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#4CAF50",
    },
    background: {
      default: "#f5f7fa",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: 0.5,
    },
    h5: {
      fontWeight: 600,
    },
    body1: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

const menuItems = [
  { 
    id: "1", 
    title: "New Pass Request", 
    icon: <Create fontSize="large" />, 
    route: "/ExitRequest",
    color: "#1976d2",
    description: "Request a new exit pass from campus"
  },
  { 
    id: "2", 
    title: "Pass History", 
    icon: <History fontSize="large" />, 
    route: "/RequestHistory",
    color: "#9c27b0",
    description: "View your previous pass requests"
  },
  { 
    id: "3", 
    title: "Student Profile", 
    icon: <Person fontSize="large" />, 
    route: "/profile",
    color: "#ff9800",
    description: "View and update your profile information"
  },
  { 
    id: "4", 
    title: "Campus Info", 
    icon: <School fontSize="large" />, 
    route: "#",
    color: "#4caf50",
    description: "Important campus information and contacts"
  },
  { 
    id: "5", 
    title: "Regulations", 
    icon: <MenuBook fontSize="large" />, 
    route: "#",
    color: "#f44336",
    description: "Student regulations and policies"
  },
];

const MainMenu = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      navigate("/"); // Navigate to login page
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        display: "flex", 
        flexDirection: "column", 
        minHeight: "100vh",
        backgroundColor: "background.default"
      }}>
        {/* App Bar with Logo and Logout */}
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <Avatar 
              src="../cosmologo.png" 
              alt="University Logo" 
              sx={{ 
                width: 50, 
                height: 50, 
                mr: 2,
                border: "2px solid white"
              }} 
            />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Cosmopolitan University
            </Typography>
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{ ml: 1 }}
            >
              <Logout />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="80%" sx={{ py: 4, flex: 1 }}>
          <Paper elevation={0} sx={{ p: isMobile ? 2 : 4, borderRadius: 4 }}>
            {/* Welcome Section */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  color: "#013a64",
                  mb: 1,
                  fontWeight: "bold"
                }}
              >
                Student Pass Portal
              </Typography>
              <Typography 
                variant="subtitle1" 
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto" }}
              >
                Manage your campus exit requests and student information
              </Typography>
            </Box>

            {/* Menu Cards */}
            <Grid container spacing={3} justifyContent="center">
              {menuItems.map((item) => (
                <Grid item key={item.id} xs={12} sm={6} md={4} lg={3}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": { 
                        transform: "translateY(-8px)",
                        boxShadow: 6,
                        cursor: "pointer" 
                      },
                    }}
                    onClick={() => navigate(item.route)}
                  >
                    <CardContent sx={{ 
                      flexGrow: 1,
                      textAlign: "center",
                      p: 3,
                    }}>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: item.color,
                          color: "white",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Typography 
                        variant="h6" 
                        component="h3" 
                        sx={{ 
                          mb: 1,
                          fontWeight: "bold"
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>

        {/* Footer */}
        <Box sx={{ 
          py: 3,
          px: 2,
          mt: "auto",
          backgroundColor: "#013a64",
          color: "primary.contrastText"
        }}>
          <Typography variant="body2" align="center">
            © {new Date().getFullYear()} Cosmopolitan University. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default MainMenu;